import { DateTime } from "luxon";
import { Button } from "melodies-source/Button";
import { Dropdown as DropdownBase } from "melodies-source/Dropdown";
import { SvgCalendar } from "melodies-source/Svgs/Calendar";
import { SvgClearCircle } from "melodies-source/Svgs/ClearCircle";
import { SvgDropdown } from "melodies-source/Svgs/Dropdown";
import { SvgLeft } from "melodies-source/Svgs/Left";
import { SvgRight } from "melodies-source/Svgs/Right";
import { Body1, Caption, Selected } from "melodies-source/Text";
import {
  HTMLInput,
  TextInput as TextInputBase,
} from "melodies-source/TextInput";
import { colors } from "melodies-source/Theme/colors";
import { useClickOutside } from "melodies-source/utils";
import { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";

const TextInput = styled(TextInputBase)`
  input {
    font-family: "Poppins", sans-serif;
    font-weight: 500;
    pointer-events: none;
    box-shadow: none;

    &:hover {
      cursor: pointer;
    }
  }
`;

const CalendarIcon = styled(SvgCalendar)`
  color: ${(p) => p.theme.colors.black60};
`;

const Dropdown = styled(DropdownBase)<{ dateOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 8px auto 0;
  box-shadow: 0 0px 8px 0 rgba(0, 0, 0, 0.14);
  padding: 20px 20px 12px;
  border: none;
  background-color: var(--component-background-color);
  ${({ dateOnly }) =>
    dateOnly &&
    css`
      width: unset;
    `}
`;

const ActionWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color);
`;

export const ActionIcon = styled.div`
  display: flex;
  border-radius: 100%;
  flex-shrink: 0;
  padding: 5px;

  :hover {
    cursor: pointer;
    background: var(--hover-background-color);
  }
`;

const MonthDateWrapper = styled.div`
  margin: 1px;
`;

const DayOfWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-top: 16px;
  ${Caption} {
    color: ${({ theme }) => theme.colors.black40};
    font-weight: 500;
  }
`;

const RightIcon = styled.svg`
  color: ${({ theme }) => theme.colors.black60};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.black80};
  }
`;

const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const weekHeaderUI = (
  <DayOfWeek>
    {days.map((day, index) => (
      <Caption style={{ textAlign: "center" }} key={index}>
        {day}
      </Caption>
    ))}
  </DayOfWeek>
);

export interface RangeValue {
  start: DateTime | null;
  end: DateTime | null;
}

export interface RangePreset {
  label: string;
  getRange: () => { start: DateTime; end: DateTime };
}

const EMPTY_PRESETS: RangePreset[] = [];

// Default preset set - opt in via the `presets` prop, not applied automatically.
export const getDefaultRangePresets = (now: DateTime): RangePreset[] => [
  { label: "Today", getRange: () => ({ start: now.startOf("day"), end: now.endOf("day") }) },
  {
    label: "Yesterday",
    getRange: () => {
      const d = now.minus({ days: 1 });
      return { start: d.startOf("day"), end: d.endOf("day") };
    },
  },
  {
    label: "Last 7 days",
    getRange: () => ({ start: now.minus({ days: 6 }).startOf("day"), end: now.endOf("day") }),
  },
  {
    label: "Last 30 days",
    getRange: () => ({ start: now.minus({ days: 29 }).startOf("day"), end: now.endOf("day") }),
  },
  {
    label: "Last 90 days",
    getRange: () => ({ start: now.minus({ days: 89 }).startOf("day"), end: now.endOf("day") }),
  },
  { label: "This month", getRange: () => ({ start: now.startOf("month"), end: now.endOf("day") }) },
  {
    label: "Last month",
    getRange: () => {
      const d = now.minus({ months: 1 });
      return { start: d.startOf("month"), end: d.endOf("month") };
    },
  },
];

const isRangeValue = (value: unknown): value is RangeValue => {
  return !!value && typeof value === "object" && ("start" in value || "end" in value);
};

function isRangePickerProps(p: Props): p is RangePickerProps {
  return p.isRangePicker;
}

function isSinglePickerProps(p: Props): p is SinglePickerProps {
  return !p.isRangePicker;
}

interface CommonProps {
  label?: string;
  onClose?: VoidFunction;
  onOpen?: VoidFunction;
  minDate?: DateTime;
  maxDate?: DateTime;
  hasError?: boolean;
  helperText?: string;
  style?: any;
  className?: string;
  renderInput?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  timeZone?: string;
  clearable?: boolean;
}

interface SinglePickerProps extends CommonProps {
  isRangePicker?: false;
  value: DateTime | null;
  type?: "dateTime" | "dateOnly";
  onChange: (v: DateTime | null) => void;
}

interface RangePickerProps extends CommonProps {
  isRangePicker: true;
  value: RangeValue | null;
  type?: "dateOnly";
  onChange: (v: RangeValue | null) => void;
  presets?: RangePreset[];
}

type Props = SinglePickerProps | RangePickerProps;

interface InputValueProps {
  date?: DateTime;
  hours: string;
  minutes: string;
  meridiem: string;
}

export const Datepicker = (props: Props) => {
  const {
    label,
    value,
    placeholder,
    onChange,
    onClose,
    onOpen,
    hasError,
    helperText,
    minDate,
    maxDate,
    renderInput,
    disabled,
    timeZone,
    type = "dateTime",
    clearable = true,
    isRangePicker = false,
  } = props;

  const [refDate, setRefDate] = useState<DateTime>(() => {
    if (!value) return DateTime.now();
    if (isRangeValue(value)) return value.start || value.end || DateTime.now();
    return value;
  });

  // time state is only relevant for Single Picker mode currently
  const [timeState, setTimeState] = useState<InputValueProps>({
    hours: "12",
    minutes: "00",
    meridiem: "AM",
  });
  const [isPickerOpen, setIsPickerOpenState] = useState(false);
  const [hoverDate, setHoverDate] = useState<DateTime | null>(null);

  const rangePresets = isRangePickerProps(props) ? props.presets ?? EMPTY_PRESETS : EMPTY_PRESETS;

  const isPresetActive = (preset: RangePreset) => {
    if (!isRangeValue(value) || !value.start || !value.end) return false;
    const range = preset.getRange();
    return (
      value.start.hasSame(range.start, "day") &&
      value.end.hasSame(range.end, "day")
    );
  };

  const handleSelectPreset = (preset: RangePreset) => {
    if (!isRangePickerProps(props)) return;
    const range = preset.getRange();
    setHoverDate(null);
    setRefDate(range.start);
    props.onChange(range);
    setIsPickerOpen(false);
  };

  useEffect(() => {
    if (timeZone) {
      setRefDate((d) => d?.setZone(timeZone));
    }
    // only parse time if it's a single value
    if (value && !isRangeValue(value)) {
      const [hours, minutes, meridiem] = value?.toFormat("h:mm:a")?.split(":");
      setTimeState({ hours, minutes, meridiem, date: value });
    }
  }, [timeZone]);

  const minDateTime = useMemo(
    () => minDate?.setZone(timeZone),
    [timeZone, minDate],
  );

  const maxDateTime = useMemo(
    () => maxDate?.setZone(timeZone),
    [timeZone, maxDate],
  );

  const setIsPickerOpen = (v: boolean) => {
    setIsPickerOpenState(v);
    if (!v) setHoverDate(null);
    onClose && !v && onClose();
    onOpen && v && onOpen();
  };

  useEffect(() => {
    if (disabled && isPickerOpen) {
      setIsPickerOpen(false);
    }
  }, [disabled, isPickerOpen]);

  const monthFirstDay = refDate.startOf("month");
  const monthLastDay = refDate.endOf("month");

  const monthDates = [];
  for (let i = 0; i < monthFirstDay.weekday; i++) monthDates.push(null);
  for (let d = 0; d < refDate.daysInMonth; d++)
    monthDates.push(monthFirstDay.plus({ days: d }));

  const gotoNextMonth = () => setRefDate(monthLastDay.plus({ days: 1 }));
  const gotoPreviousMonth = () => setRefDate(monthFirstDay.minus({ days: 1 }));

  const monthHeaderUI = (
    <ActionWrap>
      <ActionIcon onClick={gotoPreviousMonth}>
        <SvgLeft />
      </ActionIcon>
      <Selected>
        {refDate.monthLong} {refDate.year}
      </Selected>
      <ActionIcon onClick={gotoNextMonth}>
        <SvgRight />
      </ActionIcon>
    </ActionWrap>
  );

  const getDisplayString = () => {
    if (!value) return "";

    if (isRangeValue(value)) {
      const { start, end } = value;
      if (!start && !end) return "";
      const startStr = start ? start.toLocaleString(DateTime.DATE_SHORT) : "...";
      const endStr = end ? end.toLocaleString(DateTime.DATE_SHORT) : "...";
      return `${startStr} - ${endStr}`;
    }

    return type === "dateTime"
      ? value.toFormat("ff (ZZZZ)")
      : value.toLocaleString();
  };

  const valueUI = (
    <>
      <span onClick={() => !disabled && setIsPickerOpen(true)}>
        {renderInput || (
          <TextInput
            label={label}
            placeholder={placeholder || (isRangePicker ? "Start date - End date" : "Pick a date")}
            hasError={hasError}
            helperText={helperText}
            value={getDisplayString()}
            onChange={() => {}}
            leftIcon={<CalendarIcon style={{ color: colors.black40 }} />}
            rightIcon={
              disabled ? null : value ? (
                clearable && (
                  <RightIcon
                    as={SvgClearCircle}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(null);
                    }}
                  />
                )
              ) : (
                <RightIcon as={SvgDropdown} />
              )
            }
            disabled={disabled}
          />
        )}
      </span>
    </>
  );

  const handleTimeChange = (dState: DateTime, tState: InputValueProps) => {
    if (!tState.hours || !tState.minutes) {
      return;
    }

    let hours = parseInt(tState.hours);
    if (tState.meridiem !== "AM" && hours !== 12) {
      hours += 12;
    }
    if (tState.meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    const newDate = dState.set({
      hour: hours,
      minute: parseInt(tState.minutes || "0"),
    });

    if (isSinglePickerProps(props)) {
      props.onChange(newDate);
    }

    if (type === "dateOnly") setIsPickerOpen(false);
  };

  const handleDateChange = (selectedDate: DateTime) => {
    if (isRangePickerProps(props)) {
      setHoverDate(null);
      const { start, end } = isRangeValue(value) ? value : { start: null, end: null };

      let nextValue: RangeValue;

      if (!start || end) {
        nextValue = { start: selectedDate, end: null };
      } else {
        nextValue =
            selectedDate < start
              ? { start: selectedDate, end: start }
              : { start, end: selectedDate };
        setIsPickerOpen(false);
      }

      props.onChange(nextValue);
    } else {
      handleTimeChange(selectedDate, timeState);
    }
  };

  const [referenceElement, setReferenceElement] = useState<HTMLDivElement>();
  useClickOutside(
    referenceElement,
    () => isPickerOpen && setIsPickerOpen(false),
  );

  return (
    <Wrapper style={props.style} className={props.className} ref={setReferenceElement}>
      {valueUI}
      <Dropdown
        placement="bottom-start"
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        dateOnly={type === "dateOnly" || isRangePicker}
      >
        <CalendarBody>
          {isRangePicker && rangePresets.length > 0 && (
            <PresetsColumn>
              {rangePresets.map((preset) => (
                <PresetButton
                  key={preset.label}
                  type="button"
                  active={isPresetActive(preset)}
                  onClick={() => handleSelectPreset(preset)}
                >
                  {preset.label}
                </PresetButton>
              ))}
            </PresetsColumn>
          )}
          <div>
            {monthHeaderUI}
            {weekHeaderUI}
            <DayOfMonth
              onMouseLeave={() => isRangePicker && setHoverDate(null)}
            >
              {monthDates.map((date, index) => (
                <MonthDateWrapper key={index}>
                  {date && (
                    <MonthDate
                      {...{
                        date,
                        value,
                        minDate: minDateTime,
                        maxDate: maxDateTime,
                        isRangePicker,
                        hoverDate,
                        onHover: setHoverDate,
                        onChange: () => handleDateChange(date),
                      }}
                    />
                  )}
                </MonthDateWrapper>
              ))}
            </DayOfMonth>
          </div>
        </CalendarBody>

        {type === "dateTime" && !isRangePicker && !isRangeValue(value) && (
          <>
            <TimePicker
              {...{
                setTimeState: (nextTimeState) => {
                  setTimeState(nextTimeState);
                  handleTimeChange(value, nextTimeState);
                },
                timeState,
                value,
                refDate,
              }}
            />
            <DoneButton
              variant="tertiary"
              onClick={() => setIsPickerOpen(false)}
            >
              Done
            </DoneButton>
          </>
        )}
      </Dropdown>
    </Wrapper>
  );
};

const DayOfMonth = styled.div`
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(7, 1fr);
`;

const CalendarBody = styled.div`
  display: flex;
  align-items: flex-start;
`;

const PresetsColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 130px;
  margin-right: 16px;
  padding-right: 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.gray4};

  ${({ theme }) => theme.media.mobile} {
    display: none;
  }
`;

const PresetButton = styled.button<{ active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  margin: 1px 0;
  padding: 8px 10px;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color);
  white-space: nowrap;

  ${({ active }) =>
    active
      ? css`
          background-color: var(--main-color);
          color: var(--primary-button-text-color) !important;

          &:hover {
            filter: brightness(1.15);
          }
        `
      : css`
          &:hover {
            background-color: var(--hover-background-color);
          }
        `}
`;

const DoneButton = styled(Button)`
  align-self: center;
  width: auto;
  border-radius: 13px;
  height: 26px;
  padding: 3px 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--text-color) !important;
  border: 1px solid var(--text-color);

  &:hover {
    background-color: var(--hover-background-color);
  }

  &:focus {
    background-color: ${({ theme }) => theme.colors.gray2};
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  color: ${({ theme }) => theme.colors.black80};
  & > div > div:first-child {
    pointer-events: none;
  }
`;

type MonthDateProps = {
  date: DateTime;
  onChange: VoidFunction;
  isRangePicker?: boolean;
  value: DateTime | RangeValue | null;
  minDate?: DateTime;
  maxDate?: DateTime;
  hoverDate?: DateTime | null;
  onHover?: (d: DateTime | null) => void;
};

const MonthDate = ({
  value,
  date,
  minDate,
  maxDate,
  onChange,
  isRangePicker,
  hoverDate,
  onHover,
}: MonthDateProps) => {
  const isToday = date.toISODate() === DateTime.local().toISODate();
  const isPast = minDate ? date < minDate.startOf("day") : false;
  const isFuture = maxDate ? date > maxDate.endOf("day") : false;

  let isSelected = false;
  let isRangeStart = false;
  let isRangeEnd = false;
  let isInRange = false;

  let isAwaitingEnd = false;

  if (isRangePicker && isRangeValue(value)) {
     const { start, end } = value;
     const dateISO = date.toISODate();
     isAwaitingEnd = !!start && !end;

     // preview the pending end date on hover, before it's committed
     let previewStart = start;
     let previewEnd = isAwaitingEnd && hoverDate ? hoverDate : end;
     if (previewStart && previewEnd && previewEnd < previewStart) {
       [previewStart, previewEnd] = [previewEnd, previewStart];
     }

     if (previewStart && dateISO === previewStart.toISODate()) isRangeStart = true;
     if (previewStart && previewEnd && dateISO === previewEnd.toISODate()) isRangeEnd = true;

     if (previewStart && previewEnd && date > previewStart && date < previewEnd) {
       isInRange = true;
     }

     isSelected = isRangeStart || isRangeEnd;
  } else if (!isRangePicker && value && !isRangeValue(value)) {
     isSelected = date.toISODate() === value.toISODate();
  }

  const onSelect = () => {
    if (isPast || isFuture) return;
    onChange();
  };

  return (
    <MonthDateContainer
      onClick={onSelect}
      onMouseEnter={() =>
        !isPast && !isFuture && isAwaitingEnd && onHover?.(date)
      }
      {...{ isSelected, isToday, isPast, isFuture, isRangeStart, isRangeEnd, isInRange, isRangePicker }}
    >
      {date.day}
    </MonthDateContainer>
  );
};

type DayContainerProps = {
  isPast?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  isFuture?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  isInRange?: boolean;
  isRangePicker?: boolean;
};

const MonthDateContainer = styled(Body1)<DayContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  cursor: pointer;
  border-radius: 100%;
  height: 36px;
  width: 36px;
  font-weight: 400;
  color: var(--text-color);

  ${(p) =>
    (p.isPast || p.isFuture) &&
    css`
      cursor: default;
      color: var(--disabled-color);
    `}

  ${(p) =>
    p.isToday &&
    !p.isSelected &&
    !p.isInRange &&
    css`
      color: var(--main-color);
      font-weight: 500;
    `};

  ${(p) =>
    p.isSelected &&
    !p.isRangeStart && !p.isRangeEnd &&
    css`
      font-weight: 600;
      background-color: var(--main-color);
      color: var(--primary-button-text-color) !important;
    `};

  ${(p) =>
    p.isInRange &&
    css`
      background-color: var(--main-color-light, #f5f5f5);
      color: var(--text-color);
      width: 100%;
      min-width: 36px;
      border-radius: 8px;
    `}

  ${(p) =>
    (p.isRangeStart || p.isRangeEnd) &&
    css`
      font-weight: 600;
      background-color: var(--main-color);
      color: var(--primary-button-text-color) !important;
      width: 100%;
      min-width: 36px;
      border-radius: 8px;
    `}

  ${(p) =>
    !p.isSelected &&
    !p.isInRange &&
    !p.isPast &&
    css`
      :hover {
        background-color: var(--hover-background-color);
        border-radius: 100%;
      }
    `}

  /* Range picker only: darken filled cells on hover instead of swapping background. */
  ${(p) =>
    p.isRangePicker &&
    (p.isSelected || p.isInRange) &&
    !p.isPast &&
    css`
      :hover {
        filter: brightness(0.92);
      }
    `}
`;

const TimePicker = ({ setTimeState, timeState, value, refDate }) => {
  const handleHours = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueString = e.target.value;
    const value = parseInt(valueString);
    if (valueString === "" || (/^[0-9\b]+$/.test(valueString) && value <= 12)) {
      if (valueString.length === 2 && value < 1) {
        return;
      } else {
        setTimeState({
          ...timeState,
          hours: valueString,
        });
      }
      if (value > 1) {
        const nextField = document.getElementById("minutes");
        nextField && nextField.focus();
      }
    }
  };

  const handleMinutes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueString = e.target.value;
    const value = parseInt(valueString);
    if (valueString === "" || (/^[0-9\b]+$/.test(valueString) && value <= 59)) {
      if (valueString.length === 1 && value > 5) {
        setTimeState({
          ...timeState,
          minutes: `0${valueString}`,
        });
      } else {
        setTimeState({
          ...timeState,
          minutes: valueString,
        });
      }
    }
  };

  const handleMinutesBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length === 1) {
      setTimeState({
        ...timeState,
        minutes: `0${value}`,
      });
    }
  };
  return (
    <TimeWrapper>
      <Selected>
        Time {refDate && <span>{refDate.toFormat("(ZZZZ)")}</span>}
      </Selected>
      <TimeContainer>
        <InputContainer>
          <TimeInput
            type="tel"
            maxLength={2}
            placeholder="00"
            onChange={handleHours}
            value={timeState.hours}
            disabled={!value}
            onFocus={(e) => e.target.select()}
          />
          <Selected style={{ ...(!value && { color: colors.black20 }) }}>
            :
          </Selected>
          <TimeInput
            type="tel"
            min="0"
            max="59"
            placeholder="00"
            onChange={handleMinutes}
            onBlur={handleMinutesBlur}
            value={timeState.minutes}
            maxLength={2}
            disabled={!value}
            onFocus={(e) => e.target.select()}
            id="minutes"
          />
        </InputContainer>
        <InputContainer>
          <LeftButton
            active={timeState.meridiem === "AM"}
            variant={timeState.meridiem === "AM" ? "primary" : "secondary"}
            disabled={!value}
            onClick={() =>
              setTimeState({
                ...timeState,
                meridiem: "AM",
              })
            }
          >
            AM
          </LeftButton>
          <RightButton
            active={timeState.meridiem === "PM"}
            variant={timeState.meridiem === "PM" ? "primary" : "secondary"}
            disabled={!value}
            onClick={() =>
              setTimeState({
                ...timeState,
                meridiem: "PM",
              })
            }
          >
            PM
          </RightButton>
        </InputContainer>
      </TimeContainer>
    </TimeWrapper>
  );
};

const TimeWrapper = styled.div`
  display: flex;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.gray4};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  color: #ffffff;

  & > ${Selected} {
    background-color: var(--component-background-color);
    padding: 0 16px;
    display: flex;
    align-items: center;
    margin-top: -11px;
    color: var(--text-color);

    & > span {
      margin-left: 4px;
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const TimeContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: 40px;
  font-family: "Poppins", sans-serif;
  margin: 12px 0 16px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  ${Selected} {
    margin: 0 4px;
  }
`;

const TimeInput = styled(HTMLInput)`
  width: 55px;
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  ${(p) =>
    p.disabled &&
    css`
      border-color: ${({ theme }) => theme.colors.gray3};
      color: ${({ theme }) => theme.colors.black20};
    `};
`;

const LeftButton = styled(Button)<{ active?: boolean }>`
  border-radius: 6px 0 0 6px;
  width: 54px;
  font-weight: 500;

  &:hover,
  &:focus {
    background-color: var(--hover-background-color);
    border-color: var(--text-color);
    box-shadow: none;
  }
  ${({ active }) =>
    active &&
    css`
      pointer-events: none;
      &:hover,
      &:focus {
        background-color: ${({ theme }) => theme.colors.navy};
        border-color: ${({ theme }) => theme.colors.navy};
        box-shadow: none;
      }
    `}
  ${(p) =>
    p.disabled &&
    css`
      background-color: var(--component-background-color);
      border-color: ${({ theme }) => theme.colors.gray3};
      color: ${({ theme }) => theme.colors.black20};
      pointer-events: none;
    `};
`;

const RightButton = styled(LeftButton)`
  border-radius: 0 6px 6px 0;
  ${(p) =>
    p.disabled &&
    css`
      border-left: none;
    `};
`;
